import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import InstructorsManagement from "@/components/admin/InstructorsManagement";
import InstructorProposals from "@/components/admin/InstructorProposals";
import HrDashboardCard from "@/components/admin/HrDashboardCard";
import {
  ExecutivePage, PageHero, ThreeQuestions, ExecutiveSummary,
  DecisionCard, QuietSection,
  type SummaryStat,
} from "@/components/executive/system";
import { useInstructorInsights } from "@/hooks/useInstructorInsights";

export default function InstructorsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") === "proposals" ? "proposals" : "directory";
  const [tab, setTab] = useState(initial);
  const focusId = params.get("focus");
  const insights = useInstructorInsights();

  const jumpToDirectory = () => {
    setTab("directory");
    setTimeout(() => {
      document.getElementById("instructor-directory")?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const openProposals = () => {
    setTab("proposals");
    params.set("tab", "proposals");
    setParams(params, { replace: true });
  };

  const decisions = useMemo(() => {
    const out: JSX.Element[] = [];
    if (insights.missingPay.length > 0) {
      const one = insights.missingPay[0];
      const name = one.preferred_name || one.full_name;
      out.push(
        <DecisionCard
          key="pay"
          phase="needs_care"
          decision={insights.missingPay.length === 1
            ? `Set ${name}'s pay before payroll`
            : `Set pay for ${insights.missingPay.length} instructors`}
          because="Payroll can't run cleanly without a rate and payment method on file."
          primaryLabel={insights.missingPay.length === 1 ? `Open ${name}'s profile` : "Open the directory"}
          onPrimary={jumpToDirectory}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {insights.missingPay.slice(0, 8).map((i) => (
                <li key={i.id}>{i.preferred_name || i.full_name}</li>
              ))}
            </ul>
          }
        />,
      );
    }
    if (insights.missingAssignment.length > 0 && out.length < 3) {
      const one = insights.missingAssignment[0];
      const name = one.preferred_name || one.full_name;
      out.push(
        <DecisionCard
          key="assign"
          phase="growing"
          decision={insights.missingAssignment.length === 1
            ? `Assign ${name} to a class`
            : `Assign ${insights.missingAssignment.length} instructors to a class`}
          because="Active instructors without an assignment aren't showing up on any roster."
          primaryLabel="Open the directory"
          onPrimary={jumpToDirectory}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {insights.missingAssignment.slice(0, 8).map((i) => (
                <li key={i.id}>{i.preferred_name || i.full_name}</li>
              ))}
            </ul>
          }
        />,
      );
    }
    if (insights.missingW9.length > 0 && out.length < 3) {
      const one = insights.missingW9[0];
      const name = one.preferred_name || one.full_name;
      out.push(
        <DecisionCard
          key="w9"
          phase="waiting"
          decision={insights.missingW9.length === 1
            ? `Request ${name}'s W-9`
            : `Request ${insights.missingW9.length} outstanding W-9s`}
          because="W-9 is required before we can issue year-end 1099s."
          primaryLabel="Open the directory"
          onPrimary={jumpToDirectory}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {insights.missingW9.slice(0, 8).map((i) => (
                <li key={i.id}>{i.preferred_name || i.full_name}</li>
              ))}
            </ul>
          }
        />,
      );
    }
    if (insights.temporaryEndingSoon.length > 0 && out.length < 3) {
      out.push(
        <DecisionCard
          key="temp"
          phase="waiting"
          decision={`Confirm coverage — ${insights.temporaryEndingSoon.length} temporary assignment${insights.temporaryEndingSoon.length === 1 ? "" : "s"} ending soon`}
          because="Temporary coverage ends within two weeks. Line up a permanent instructor."
          primaryLabel="Open the directory"
          onPrimary={jumpToDirectory}
          evidence={
            <ul className="list-disc pl-4 space-y-1">
              {insights.temporaryEndingSoon.slice(0, 6).map((t) => (
                <li key={t.instructor_id + t.end_date}>
                  {t.instructor_name} — through {new Date(t.end_date).toLocaleDateString()}
                </li>
              ))}
            </ul>
          }
        />,
      );
    }
    if (insights.duplicateCount > 0 && out.length < 3) {
      out.push(
        <DecisionCard
          key="dupes"
          phase="needs_care"
          decision={`Merge ${insights.duplicateCount} possible duplicate${insights.duplicateCount === 1 ? "" : "s"}`}
          because="Duplicate profiles split assignments and payments across two records."
          primaryLabel="Open the directory"
          onPrimary={jumpToDirectory}
        />,
      );
    }
    if (insights.proposalsWaitingCount > 0 && out.length < 3) {
      out.push(
        <DecisionCard
          key="proposals"
          phase="growing"
          decision={`Review ${insights.proposalsWaitingCount} instructor proposal${insights.proposalsWaitingCount === 1 ? "" : "s"}`}
          because="Prospective instructors are waiting on your review."
          primaryLabel="Open proposals"
          onPrimary={openProposals}
        />,
      );
    }
    return out.slice(0, 3);
  }, [insights]);

  const summary: SummaryStat[] = [
    { label: "Active instructors", value: insights.activeCount, hideIfZero: true },
    { label: "Ready to teach", value: insights.readyCount, hideIfZero: true },
    { label: "Need something", value: insights.needsCareCount, hideIfZero: true },
    { label: "Missing an assignment", value: insights.missingAssignment.length, hideIfZero: true },
    { label: "Prospects", value: insights.prospects.length, hideIfZero: true },
    { label: "Proposals waiting", value: insights.proposalsWaitingCount, hideIfZero: true },
  ];

  const whatChanged = insights.recentlyReady > 0
    ? `${insights.recentlyReady} instructor profile${insights.recentlyReady === 1 ? " was" : "s were"} updated this week.`
    : "No profile changes in the last week.";
  const needsAttention = insights.needsCareCount > 0
    ? `${insights.needsCareCount} of ${insights.activeCount} active instructors need something.`
    : insights.activeCount === 0 ? "No active instructors yet." : "Every active instructor is ready to teach.";
  const doNext = decisions.length > 0
    ? (decisions[0].props as any).decision
    : insights.prospects.length > 0
      ? `Onboard ${insights.prospects.length} prospect${insights.prospects.length === 1 ? "" : "s"}`
      : "Everything looks tended.";

  return (
    <ExecutivePage section="instructors">
      <UnseenReviewBar
        badge="instructors"
        note="Marking these read only clears the sidebar bubble — no proposal is approved, declined or changed."
      />

      <PageHero
        eyebrow="🌿 Instructors"
        title="Who's ready to teach, and who still needs care."
        subtitle="One profile per person. Readiness is derived — no double-entry."
        actions={
          <Button onClick={openProposals}>
            <UserPlus className="h-4 w-4 mr-1" /> Review proposals
          </Button>
        }
      />

      <ThreeQuestions
        whatChanged={whatChanged}
        needsAttention={needsAttention}
        doNext={doNext}
        attentionCount={insights.needsCareCount}
      />

      <ExecutiveSummary stats={summary} />

      {decisions.length > 0 && (
        <QuietSection
          eyebrow="Today's Focus"
          title="Three things worth your attention"
          description="Pay before payroll · Assignments before rosters · Documents before 1099s."
          paper={false}
        >
          <div className="grid gap-3 md:grid-cols-3">{decisions}</div>
        </QuietSection>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="directory">Directory</TabsTrigger>
          <TabsTrigger value="proposals">
            Proposals{insights.proposalsWaitingCount > 0 ? ` · ${insights.proposalsWaitingCount}` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="directory" className="mt-4 space-y-4">
          <HrDashboardCard />
          <div id="instructor-directory">
            <InstructorsManagement />
          </div>
        </TabsContent>
        <TabsContent value="proposals" className="mt-4">
          <InstructorProposals userId={user?.id ?? null} focusId={focusId} />
        </TabsContent>
      </Tabs>
    </ExecutivePage>
  );
}
